import { useMemo, useState } from 'react';
import { matchesSearch } from '@atlas/shared';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { alpha } from '@mui/material/styles';
import {
  Box,
  Divider,
  InputAdornment,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  TextField,
  Typography,
} from '@mui/material';
import { SearchIcon, ArrowBackIcon, AdminPanelSettingsIcon } from '@atlas/ui';
import type { AdminCategoryGroup } from './adminTabs.config';

export interface AdminSideNavProps {
  /** Visible tabs grouped by category (from `useVisibleAdminTabs`). */
  groups: AdminCategoryGroup[];
  /** `value` of the currently active tool, or `false` when none is selected. */
  activeValue: string | false;
  /** Called after any nav click — used to close the mobile drawer. */
  onNavigate?: () => void;
}

/**
 * Full contents of the admin side menu, rendered inside `AdminSideMenu`'s
 * drawer. While the user is in the admin area this REPLACES the app's primary
 * side nav (rather than sitting beside it), so the tool content gets the full
 * width.
 *
 * Top to bottom: an "Exit admin" row back to the main app, an "Admin" header
 * with a tool/area count, a pinned "filter tools" box, then every admin tool
 * listed once under its category header. The category groups are a flat,
 * always-visible list (no collapsing). The active tool gets an indigo
 * left-accent + tinted row. Each item is a `RouterLink`, so deep links and
 * back/forward behave exactly as before.
 */
export function AdminSideNav({ groups, activeValue, onNavigate }: AdminSideNavProps) {
  const { t } = useTranslation();
  const [filter, setFilter] = useState('');

  const toolCount = useMemo(
    () => groups.reduce((sum, group) => sum + group.tabs.length, 0),
    [groups],
  );

  const query = filter.trim().toLowerCase();
  const filteredGroups = useMemo(() => {
    if (!query) return groups;
    return groups
      .map((group) => ({
        ...group,
        tabs: group.tabs.filter((tab) =>
          matchesSearch(t(tab.labelKey), query),
        ),
      }))
      .filter((group) => group.tabs.length > 0);
  }, [groups, query, t]);

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Exit back to the main app */}
      <ListItemButton
        component={RouterLink}
        to="/"
        onClick={onNavigate}
        sx={{
          // ListItemButton defaults to flex-grow:1; as a direct flex-column
          // child that stretches it to fill the sidebar. Pin it to its content.
          flex: '0 0 auto',
          px: 2,
          py: 1,
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <ListItemIcon sx={{ minWidth: 32, color: 'text.secondary' }}>
          <ArrowBackIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText
          primary={t('admin.sideNav.exit')}
          secondary={t('admin.sideNav.exitHint')}
          slotProps={{
            primary: { variant: 'body2', fontWeight: 600 },
            secondary: { variant: 'caption' },
          }}
        />
      </ListItemButton>

      {/* Admin header + tool/area count */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, px: 2, pt: 1.5, pb: 1 }}>
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 34,
            height: 34,
            borderRadius: 1.5,
            bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1),
            color: 'primary.main',
          }}
        >
          <AdminPanelSettingsIcon fontSize="small" />
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="subtitle2" fontWeight={700} lineHeight={1.2}>
            {t('admin.layout.pageTitle')}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {t('admin.sideNav.summary', {
              tools: toolCount,
              areas: groups.length,
            })}
          </Typography>
        </Box>
      </Box>

      {/* Filter */}
      <Box sx={{ px: 1.5, pb: 1 }}>
        <TextField
          value={filter}
          onChange={(event) => setFilter(event.target.value)}
          placeholder={t('admin.sideNav.filterPlaceholder')}
          aria-label={t('admin.sideNav.filterPlaceholder')}
          size="small"
          fullWidth
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            },
          }}
        />
      </Box>
      <Divider />

      {/* Grouped tool list */}
      <Box
        component="nav"
        aria-label={t('admin.sideNav.ariaLabel')}
        sx={{ flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden', px: 1, py: 1 }}
      >
        {filteredGroups.length === 0 ? (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ textAlign: 'center', py: 3, px: 1 }}
          >
            {t('admin.sideNav.noMatch')}
          </Typography>
        ) : (
          filteredGroups.map((group) => (
            <Box key={group.category.id} sx={{ mb: 1 }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.75,
                  px: 1.25,
                  pt: 1,
                  pb: 0.5,
                  color: 'text.disabled',
                }}
              >
                <Box sx={{ display: 'inline-flex', '& svg': { fontSize: 15 } }}>
                  {group.category.icon}
                </Box>
                <Typography
                  variant="overline"
                  sx={{
                    lineHeight: 1.4,
                    fontWeight: 700,
                    letterSpacing: '0.06em',
                    color: 'text.secondary',
                  }}
                >
                  {t(group.category.labelKey)}
                </Typography>
              </Box>

              <List dense disablePadding>
                {group.tabs.map((tab) => {
                  const selected = tab.value === activeValue;
                  return (
                    <ListItemButton
                      key={tab.value}
                      component={RouterLink}
                      to={tab.path}
                      onClick={onNavigate}
                      selected={selected}
                      aria-current={selected ? 'page' : undefined}
                      sx={{
                        borderRadius: 1,
                        mb: 0.25,
                        pl: 1.25,
                        borderLeft: '3px solid transparent',
                        '&.Mui-selected': {
                          bgcolor: (theme) =>
                            alpha(theme.palette.primary.main, 0.08),
                          borderLeftColor: 'primary.main',
                          color: 'primary.main',
                          '& .MuiListItemIcon-root': { color: 'primary.main' },
                          '&:hover': {
                            bgcolor: (theme) =>
                              alpha(theme.palette.primary.main, 0.12),
                          },
                        },
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 30, color: 'text.secondary' }}>
                        {tab.icon}
                      </ListItemIcon>
                      <ListItemText
                        primary={t(tab.labelKey)}
                        slotProps={{
                          primary: {
                            variant: 'body2',
                            fontWeight: selected ? 600 : 500,
                          },
                        }}
                      />
                    </ListItemButton>
                  );
                })}
              </List>
            </Box>
          ))
        )}
      </Box>
    </Box>
  );
}

export default AdminSideNav;
